import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {brandT} from '@/lib/types/brand.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderBrandTableStats} from '@/lib/config/brandTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useRetailer} from '@/pages/(shared)/retailers/detail/providers/retailerDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

/**
 * Retailer Market Detail Component
 */
export const RetailerDetailBrands = () => {
  const {toast} = useNotifications();
  const {isAdmin} = useAuth();
  const {retailer, searchBrandsToAdd, addBrandToRetailer, removeBrandFromRetailer} = useRetailer();
  const {data: stats} = useViewQuery({view: 'brand_stats'});

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<brandT[]>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<brandT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Retailer_Brand',
    title: 'Brands Summary',
    columns: ['fk_retailer_id', 'date_removed', ['Brand', ['brand_id', 'brand_name', 'brand_description']]],
    searchColumn: 'Brand.brand_name',
    filters: [{column: 'fk_retailer_id', operator: 'eq', value: retailer?.retailer_id}],
    addButtonLabel: 'Add Brand to Retailer',
    noResultsMessage: 'No brands associated with retailer',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {
        header: 'Status',
        field: 'date_removed',
        render: brand => (
          <span className={`badge bg-${!brand.date_removed ? 'success' : 'danger'} text-white`}>
            {!brand.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Brand.brand_name',
        render: row => (
          <Link to={`/brands/${row.Brand.brand_id}`} className="text-primary hover:text-primary dark:text-primary">
            {row.Brand.brand_name}
          </Link>
        ),
      },
      {header: 'Description', field: 'Brand.brand_description'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Brand;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderBrandTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchBrandsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (brand: brandT) => {
    try {
      await addBrandToRetailer(brand.brand_id);
      toast.success('Brand Added To Retailer');
      dataTableRef.current?.refetch();
    } catch (error) {
      console.error(error);
      toast.error('Error Adding Brand To Retailer');
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Retailer not found');

    try {
      await removeBrandFromRetailer(dataRowToDeleteRef.current.brand_id);
      toast.success('Brand Removed From Retailer');
      dataTableRef.current?.refetch();
    } catch (error) {
      console.error(error);
      toast.error('Error Removing Brand From Retailer');
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Brands"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Brand Name', field: 'brand_name'}],
          valueField: 'brand_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this brand from the Retailer?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
